import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState, type JSX, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Copy,
  History,
  Inbox,
  MoreHorizontal,
  Pencil,
  Send,
  Trash2,
  Undo2,
  Users,
} from 'lucide-react';
import clsx from 'clsx';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useToast } from '../lib/toast';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { useConfirm } from '../components/ui/Confirm';
import { Drawer } from '../components/ui/Drawer';
import { TextField } from '../components/ui/Field';
import { GatedSection } from '../components/shared/GatedSection';
import { DetailHeader } from '../components/shared/DetailHeader';
import { KpiStrip, type KpiItem } from '../components/shared/KpiStrip';
import { RelationPanel } from '../components/shared/RelationPanel';
import { ActivityPanel } from '../components/shared/ActivityPanel';
import { formatDate, timeAgo } from '../lib/format';
import {
  ComposerDrawer,
  type ComposerDrawerInitial,
} from '../components/announcements/ComposerDrawer';
import { WithdrawDrawer } from '../components/announcements/WithdrawDrawer';
import {
  TARGETABLE_ROLES,
  type AnnouncementDetail,
  type AnnouncementRecipientsResponse,
  type AnnouncementTargetRecord,
} from '../components/system/types';

export const Route = createFileRoute('/_authed/announcements/$announcementId')({
  component: AnnouncementDetailPage,
});

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type Tab = 'overview' | 'delivery' | 'activity';

const TABS: ReadonlyArray<{ value: Tab; label: string; icon: typeof Users }> = [
  { value: 'overview', label: 'Overview', icon: Inbox },
  { value: 'delivery', label: 'Delivery', icon: Users },
  { value: 'activity', label: 'Activity', icon: History },
];

type DerivedStatus = 'draft' | 'published';

const deriveStatus = (detail: AnnouncementDetail): DerivedStatus =>
  detail.publishedAt ? 'published' : 'draft';

function AnnouncementDetailPage(): JSX.Element {
  const { announcementId } = Route.useParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const navigate = useNavigate();
  const primaryRole = useAuthStore((s) => s.user?.primaryRole ?? null);
  const canManage =
    primaryRole === 'super_admin' || primaryRole === 'academic_admin';

  const [tab, setTab] = useState<Tab>('overview');
  const [editOpen, setEditOpen] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [resendOpen, setResendOpen] = useState(false);

  const detailQuery = useQuery({
    queryKey: ['announcement', 'detail', announcementId] as const,
    queryFn: ({ signal }) =>
      apiRequest<AnnouncementDetail>(
        `/api/announcements/${encodeURIComponent(announcementId)}`,
        { signal },
      ),
    retry: (failureCount, err) => {
      // 404 is the "you're not a recipient" signal too — no point retrying.
      if (
        err instanceof ApiError &&
        (err.status === 404 || err.status === 403)
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const detail = detailQuery.data;

  const invalidate = (): void => {
    void queryClient.invalidateQueries({
      queryKey: ['announcement', 'detail', announcementId],
    });
    void queryClient.invalidateQueries({
      queryKey: ['announcement', 'recipients', announcementId],
    });
    void queryClient.invalidateQueries({ queryKey: ['announcements'] });
  };

  const handleCopyLink = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast('Link copied.', { variant: 'success' });
    } catch {
      toast('Could not copy link.', { variant: 'error' });
    }
  };

  // ---- Write-path mutations -------------------------------------------

  const duplicateMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ id: string }>(
        `/api/announcements/${encodeURIComponent(announcementId)}/duplicate`,
        { method: 'POST' },
      ),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['announcements'] });
      toast('Duplicate draft created.', { variant: 'success' });
      void navigate({
        to: '/announcements/$announcementId',
        params: { announcementId: res.id },
      });
    },
    onError: (err) => {
      toast(
        err instanceof ApiError ? err.message : 'Could not duplicate announcement.',
        { variant: 'error' },
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () =>
      apiRequest<void>(
        `/api/announcements/${encodeURIComponent(announcementId)}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['announcements'] });
      toast('Announcement deleted.', { variant: 'success' });
      void navigate({ to: '/announcements' });
    },
    onError: (err) => {
      toast(
        err instanceof ApiError ? err.message : 'Could not delete announcement.',
        { variant: 'error' },
      );
    },
  });

  const handleDuplicate = (): void => {
    setOverflowOpen(false);
    void duplicateMutation.mutateAsync();
  };

  const handleDelete = async (): Promise<void> => {
    setOverflowOpen(false);
    const ok = await confirm({
      title: 'Delete announcement?',
      description:
        'This permanently removes the draft. Only unpublished drafts can be deleted.',
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    void deleteMutation.mutateAsync();
  };

  const kpiItems = useMemo<KpiItem[]>(() => {
    if (!detail) return [];
    const { stats, sendPush, sendEmail, publishedAt } = detail;
    return [
      {
        label: 'Recipients',
        value: stats.recipientCount,
      },
      {
        label: 'Read',
        value: `${stats.readPercent}%`,
        sub:
          stats.recipientCount > 0
            ? `${stats.readCount} of ${stats.recipientCount}`
            : 'no recipients',
      },
      {
        // Per-channel fanout counts are not persisted today — we mirror
        // the announcement's `sendPush` flag against the recipient total
        // so an operator can tell at a glance whether push went out.
        label: 'Push',
        value: sendPush ? stats.recipientCount : 'Off',
        sub: sendPush ? 'in the portal and on phones' : 'no notifications sent',
      },
      // A draft, or one where nothing was queued, has no emails to report on.
      !sendEmail
        ? { label: 'Email', value: 'Off', sub: 'not sent by email' }
        : !publishedAt || !stats.emails || stats.emails.sent + stats.emails.waiting + stats.emails.failed === 0
          ? { label: 'Email', value: 'None', sub: 'No emails were sent for this announcement' }
          : {
              label: 'Email',
              value: `${stats.emails.sent} sent`,
              sub: stats.emails.waiting
                ? `${stats.emails.waiting} waiting · go out within the hour`
                : stats.emails.failed
                  ? `${stats.emails.failed} couldn’t be sent`
                  : 'all sent',
            },
      {
        label: 'Published',
        value: publishedAt ? timeAgo(publishedAt) : 'Draft',
      },
    ];
  }, [detail]);

  // Pass the detail payload straight to the composer so the audience
  // picker can pre-fill from `targets[]` rather than the targetless
  // list-row summary.
  const composerInitial: ComposerDrawerInitial | null =
    editOpen && detail ? detail : null;

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto w-full">
      <GatedSection
        error={detailQuery.error}
        title="Announcement"
        onRetry={() => detailQuery.refetch()}
      >
        {detailQuery.isLoading || !detail ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <DetailHeader
              title={detail.title}
              sublabel={
                <>
                  <span>
                    {detail.publishedAt
                      ? `Published ${timeAgo(detail.publishedAt)}`
                      : 'Draft'}
                  </span>
                  {detail.createdByName ? (
                    <>
                      <span aria-hidden="true">·</span>
                      <span>by {detail.createdByName}</span>
                    </>
                  ) : null}
                </>
              }
              statusChip={<StatusChip status={deriveStatus(detail)} />}
              backTo="/announcements"
              actions={
                canManage ? (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<Pencil size={14} aria-hidden="true" />}
                      onClick={() => setEditOpen(true)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<Copy size={14} aria-hidden="true" />}
                      onClick={() => {
                        void handleCopyLink();
                      }}
                    >
                      Copy link
                    </Button>
                    <OverflowMenu
                      open={overflowOpen}
                      onOpenChange={setOverflowOpen}
                      detail={detail}
                      pending={
                        duplicateMutation.isPending || deleteMutation.isPending
                      }
                      onDuplicate={handleDuplicate}
                      onWithdraw={() => {
                        setOverflowOpen(false);
                        setWithdrawOpen(true);
                      }}
                      onResend={() => {
                        setOverflowOpen(false);
                        setResendOpen(true);
                      }}
                      onSchedule={() => {
                        setOverflowOpen(false);
                        setScheduleOpen(true);
                      }}
                      onDelete={() => {
                        void handleDelete();
                      }}
                    />
                  </>
                ) : null
              }
            />

            <KpiStrip items={kpiItems} />

            <div
              role="tablist"
              aria-label="Announcement sections"
              className="flex flex-wrap gap-1 border-b border-[color:var(--color-border)]"
            >
              {TABS.map(({ value, label, icon: Icon }) => {
                const active = tab === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTab(value)}
                    className={clsx(
                      'inline-flex items-center gap-1.5 rounded-t-md px-3 py-2 text-sm font-medium transition-colors',
                      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
                      active
                        ? 'border-b-2 border-gold-500 text-[color:var(--color-text)]'
                        : 'text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]',
                    )}
                  >
                    <Icon size={14} aria-hidden="true" />
                    {label}
                  </button>
                );
              })}
            </div>

            {tab === 'overview' ? <OverviewTab detail={detail} /> : null}
            {tab === 'delivery' ? (
              <DeliveryTab
                announcementId={detail.id}
                canFilter={canManage}
              />
            ) : null}
            {tab === 'activity' ? (
              <ActivityPanel
                targetType="announcement"
                targetId={detail.id}
                title="Activity"
              />
            ) : null}
          </div>
        )}
      </GatedSection>

      {canManage ? (
        <>
          <ComposerDrawer
            open={editOpen}
            onClose={() => setEditOpen(false)}
            initial={composerInitial}
            onPublished={() => {
              setEditOpen(false);
              invalidate();
              toast('Announcement published.', { variant: 'success' });
            }}
          />
          <WithdrawDrawer
            open={withdrawOpen}
            onClose={() => setWithdrawOpen(false)}
            announcementId={announcementId}
          />
          {detail ? (
            <>
              <ScheduleDrawer
                open={scheduleOpen}
                onClose={() => setScheduleOpen(false)}
                announcementId={announcementId}
                currentScheduledFor={detail.scheduledFor ?? null}
              />
              <ResendDialog
                open={resendOpen}
                onClose={() => setResendOpen(false)}
                announcementId={announcementId}
                totalRecipients={detail.stats.recipientCount}
              />
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header status chip
// ---------------------------------------------------------------------------

function StatusChip({ status }: { status: DerivedStatus }): JSX.Element {
  if (status === 'draft') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 text-xs font-semibold text-[color:var(--color-text-muted)]">
        Draft
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-[color:var(--color-success)]/40 bg-[color:var(--color-success)]/10 px-2 py-0.5 text-xs font-semibold text-[color:var(--color-success)]">
      Published
    </span>
  );
}

// ---------------------------------------------------------------------------
// Overview tab
// ---------------------------------------------------------------------------

function OverviewTab({ detail }: { detail: AnnouncementDetail }): JSX.Element {
  return (
    <div className="space-y-4">
      {detail.withdrawnAt ? <WithdrawnBanner detail={detail} /> : null}
      {detail.scheduledFor && !detail.publishedAt ? (
        <ScheduledBanner scheduledFor={detail.scheduledFor} />
      ) : null}
      <div className="grid gap-4 lg:grid-cols-3">
        <section
          aria-labelledby="announcement-body-heading"
          className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5 lg:col-span-2"
        >
          <h3
            id="announcement-body-heading"
            className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]"
          >
            Message
          </h3>
          <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-[color:var(--color-text)]">
            {detail.body}
          </p>
        </section>

        <div className="space-y-4">
          <CreatedByCard detail={detail} />
          <TargetsCard targets={detail.targets} />
        </div>
      </div>
    </div>
  );
}

/**
 * Withdrawn banner shown on the Overview tab when `withdrawnAt` is set.
 * Renders defensively — every embedded field is optional + nullable
 * so a partial backend payload degrades to an em-dash rather than a
 * runtime error.
 */
function WithdrawnBanner({
  detail,
}: {
  detail: AnnouncementDetail;
}): JSX.Element {
  const withdrawnAt = detail.withdrawnAt ?? null;
  const actor =
    detail.withdrawnByRef?.displayName ??
    (detail.withdrawnBy ? `User ${detail.withdrawnBy.slice(0, 8)}` : null);
  const reason = detail.withdrawnReason ?? null;
  return (
    <section
      role="alert"
      aria-label="Withdrawal notice"
      className="rounded-xl border border-danger/40 bg-danger/10 p-4 text-sm text-[color:var(--color-text)]"
    >
      <div className="flex items-start gap-3">
        <Undo2
          size={16}
          aria-hidden="true"
          className="mt-0.5 shrink-0 text-danger"
        />
        <div className="min-w-0">
          <p className="font-semibold text-danger">
            Withdrawn{withdrawnAt ? ` ${timeAgo(withdrawnAt)}` : ''}
            {actor ? ` by ${actor}` : ''}.
          </p>
          <p className="mt-1 text-[color:var(--color-text-muted)]">
            Reason: {reason && reason.length > 0 ? reason : '—'}
          </p>
        </div>
      </div>
    </section>
  );
}

function ScheduledBanner({
  scheduledFor,
}: {
  scheduledFor: string;
}): JSX.Element {
  return (
    <section
      role="status"
      aria-label="Scheduled notice"
      className="rounded-xl border border-gold-500/40 bg-gold-50/40 p-4 text-sm text-[color:var(--color-text)]"
    >
      <div className="flex items-start gap-3">
        <Calendar
          size={16}
          aria-hidden="true"
          className="mt-0.5 shrink-0 text-gold-700"
        />
        <div className="min-w-0">
          <p className="font-semibold text-gold-700">
            Scheduled for {formatDate(scheduledFor, { withTime: true })}
          </p>
          <p className="mt-1 text-[color:var(--color-text-muted)]">
            It will be published and sent out automatically at that time.
          </p>
        </div>
      </div>
    </section>
  );
}

function CreatedByCard({ detail }: { detail: AnnouncementDetail }): JSX.Element {
  return (
    <section
      aria-labelledby="announcement-created-by-heading"
      className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5"
    >
      <h3
        id="announcement-created-by-heading"
        className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]"
      >
        Created by
      </h3>
      <dl className="mt-3 space-y-2 text-sm">
        <DetailRow
          label="Author"
          value={detail.createdByName ?? '—'}
        />
        <DetailRow
          label="User ID"
          value={detail.createdBy}
          mono
        />
        <DetailRow
          label="Created"
          value={timeAgo(detail.createdAt)}
        />
        {detail.updatedAt !== detail.createdAt ? (
          <DetailRow
            label="Last updated"
            value={timeAgo(detail.updatedAt)}
          />
        ) : null}
      </dl>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Targets card
// ---------------------------------------------------------------------------

const ROLE_LABEL_BY_VALUE: ReadonlyMap<string, string> = new Map(
  TARGETABLE_ROLES.map((opt) => [opt.value, opt.label]),
);

interface RenderedTarget {
  id: string;
  label: string;
  caption: string;
}

const renderTarget = (target: AnnouncementTargetRecord): RenderedTarget => {
  if (target.targetType === 'all_users') {
    return { id: target.id, label: 'Everyone', caption: 'All users' };
  }
  if (target.targetType === 'role') {
    const label =
      (target.targetValue && ROLE_LABEL_BY_VALUE.get(target.targetValue)) ||
      target.targetValue ||
      'Role';
    return { id: target.id, label, caption: 'Role' };
  }
  if (target.targetType === 'batch') {
    return {
      id: target.id,
      label: target.targetValue ?? 'Batch',
      caption: 'Batch',
    };
  }
  return {
    id: target.id,
    label: target.targetValue ?? 'Family',
    caption: 'Family',
  };
};

function TargetsCard({
  targets,
}: {
  targets: ReadonlyArray<AnnouncementTargetRecord>;
}): JSX.Element {
  const rendered = useMemo(() => targets.map(renderTarget), [targets]);
  return (
    <section
      aria-labelledby="announcement-targets-heading"
      className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5"
    >
      <h3
        id="announcement-targets-heading"
        className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]"
      >
        Targets
      </h3>
      {rendered.length === 0 ? (
        <p className="mt-3 text-sm text-[color:var(--color-text-muted)]">
          No targets attached.
        </p>
      ) : (
        <ul className="mt-3 flex flex-wrap gap-2" role="list">
          {rendered.map((t) => (
            <li key={t.id}>
              <span
                className="inline-flex items-center gap-2 rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2.5 py-1 text-xs"
                title={`${t.caption}: ${t.label}`}
              >
                <span className="text-[10px] font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                  {t.caption}
                </span>
                <span className="font-medium text-[color:var(--color-text)]">
                  {t.label}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Delivery tab
// ---------------------------------------------------------------------------

type DeliveryFilter = 'all' | 'read' | 'unread';

const PAGE_SIZE = 50;

interface DeliveryTabProps {
  announcementId: string;
  canFilter: boolean;
}

const FILTER_TABS: ReadonlyArray<{ value: DeliveryFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'read', label: 'Read' },
  { value: 'unread', label: 'Unread' },
];

function DeliveryTab({
  announcementId,
  canFilter,
}: DeliveryTabProps): JSX.Element {
  const [filter, setFilter] = useState<DeliveryFilter>('all');
  const [offset, setOffset] = useState(0);

  const query = useQuery({
    queryKey: [
      'announcement',
      'recipients',
      announcementId,
      filter,
      offset,
    ] as const,
    queryFn: ({ signal }) =>
      apiRequest<AnnouncementRecipientsResponse>(
        `/api/announcements/${encodeURIComponent(announcementId)}/recipients`,
        {
          signal,
          query: {
            limit: PAGE_SIZE,
            offset,
            ...(filter === 'all' ? {} : { status: filter }),
          },
        },
      ),
    placeholderData: (prev) => prev,
    retry: (failureCount, err) => {
      if (
        err instanceof ApiError &&
        (err.status === 404 || err.status === 403)
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const scope = query.data?.scope ?? null;
  const isEmpty = !query.isLoading && items.length === 0;
  const canPrev = offset > 0;
  const canNext = offset + items.length < total;

  const errorState =
    query.isError && !query.data
      ? {
          message:
            query.error instanceof ApiError
              ? query.error.message
              : 'Could not load recipients. Please try again.',
          onRetry: () => {
            void query.refetch();
          },
        }
      : null;

  const description: ReactNode = (() => {
    if (scope === 'self') return 'Showing your delivery only.';
    if (query.data) {
      const start = items.length === 0 ? 0 : offset + 1;
      const end = offset + items.length;
      return `Showing ${start}–${end} of ${total} recipient${total === 1 ? '' : 's'}.`;
    }
    return null;
  })();

  return (
    <RelationPanel
      title="Recipients"
      count={query.data ? total : undefined}
      description={description}
      loading={query.isLoading && !query.data}
      error={errorState}
      empty={{ copy: 'No recipients match this filter.' }}
      isEmpty={isEmpty}
      action={
        canFilter ? (
          <div
            role="tablist"
            aria-label="Filter recipients by read state"
            className="flex flex-wrap gap-1"
          >
            {FILTER_TABS.map((opt) => {
              const isOn = filter === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="tab"
                  aria-selected={isOn}
                  onClick={() => {
                    setFilter(opt.value);
                    setOffset(0);
                  }}
                  className={clsx(
                    'inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
                    isOn
                      ? 'border-gold-500 bg-gold-50 text-gold-700'
                      : 'border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] text-[color:var(--color-text-muted)] hover:border-gold-500 hover:text-[color:var(--color-text)]',
                  )}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        ) : null
      }
    >
      <div className="-mx-3 overflow-x-auto sm:mx-0">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="text-[10px] font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
            <tr>
              <th scope="col" className="px-3 py-2">Recipient</th>
              <th scope="col" className="px-3 py-2">Email</th>
              <th scope="col" className="px-3 py-2">Sent</th>
              <th scope="col" className="px-3 py-2">Read</th>
              <th scope="col" className="px-3 py-2">Channel</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <RecipientRow key={row.notificationId} row={row} />
            ))}
          </tbody>
        </table>
      </div>
      {total > 0 ? (
        <nav
          aria-label="Recipients pagination"
          className="mt-4 flex items-center justify-end gap-2"
        >
          <Button
            variant="secondary"
            size="sm"
            icon={<ChevronLeft size={14} aria-hidden="true" />}
            onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            disabled={!canPrev}
            aria-label="Previous page"
          >
            Prev
          </Button>
          <span
            aria-live="polite"
            className="px-2 text-xs tabular-nums text-[color:var(--color-text-muted)]"
          >
            {items.length === 0
              ? '0'
              : `${offset + 1}–${offset + items.length}`}{' '}
            of {total}
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setOffset((o) => o + PAGE_SIZE)}
            disabled={!canNext}
            aria-label="Next page"
          >
            Next
            <ChevronRight size={14} aria-hidden="true" />
          </Button>
        </nav>
      ) : null}
    </RelationPanel>
  );
}

function RecipientRow({
  row,
}: {
  row: AnnouncementRecipientsResponse['items'][number];
}): JSX.Element {
  return (
    <tr className="border-t border-[color:var(--color-border)]">
      <td className="px-3 py-2 align-middle">
        <span className="font-medium text-[color:var(--color-text)]">
          {row.userName ?? '—'}
        </span>
      </td>
      <td className="px-3 py-2 align-middle">
        <span className="font-mono text-xs text-[color:var(--color-text-muted)]">
          {row.userEmail ?? '—'}
        </span>
      </td>
      <td className="px-3 py-2 align-middle text-xs text-[color:var(--color-text-muted)]">
        {row.sentAt ? timeAgo(row.sentAt) : timeAgo(row.createdAt)}
      </td>
      <td className="px-3 py-2 align-middle text-xs">
        {row.readAt ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-[color:var(--color-success)]/40 bg-[color:var(--color-success)]/10 px-2 py-0.5 text-[11px] font-semibold text-[color:var(--color-success)]">
            {timeAgo(row.readAt)}
          </span>
        ) : (
          <span className="text-[color:var(--color-text-muted)]">—</span>
        )}
      </td>
      <td className="px-3 py-2 align-middle text-xs">
        <ChannelChip />
      </td>
    </tr>
  );
}

/**
 * Per-row channel chip. Per-channel fanout isn't recorded today (PR 15
 * introduces `announcement_emails`); every persisted recipient lives in
 * the `notifications` table, so the chip reflects that single channel.
 */
function ChannelChip(): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 text-[11px] font-medium text-[color:var(--color-text-muted)]">
      In-app
    </span>
  );
}

// ---------------------------------------------------------------------------
// Detail row primitive
// ---------------------------------------------------------------------------

function DetailRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <dt className="text-xs text-[color:var(--color-text-muted)]">{label}</dt>
      <dd
        className={clsx(
          'min-w-0 text-right text-[color:var(--color-text)]',
          mono && 'font-mono text-xs break-all',
        )}
      >
        {value}
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header overflow menu
//
// Mirrors the inline pattern in `_authed.academics.batches.$batchId.tsx`:
// `data-overflow-menu` on the root + an outside-click listener that closes
// the popup. Each item is independently gated so the menu collapses to the
// minimum useful set for the current announcement state.
// ---------------------------------------------------------------------------

interface OverflowMenuProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  detail: AnnouncementDetail | undefined;
  pending: boolean;
  onDuplicate: () => void;
  onWithdraw: () => void;
  onResend: () => void;
  onSchedule: () => void;
  onDelete: () => void;
}

function OverflowMenu({
  open,
  onOpenChange,
  detail,
  pending,
  onDuplicate,
  onWithdraw,
  onResend,
  onSchedule,
  onDelete,
}: OverflowMenuProps): JSX.Element {
  // Close on outside click (mousedown so we beat the menu item's own click).
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      const target = e.target as HTMLElement | null;
      if (!target?.closest('[data-overflow-menu]')) onOpenChange(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onOpenChange]);

  const items = useMemo(() => {
    if (!detail) return [];
    const published = Boolean(detail.publishedAt);
    const withdrawn = Boolean(detail.withdrawnAt);
    const entries: Array<{
      key: string;
      label: string;
      icon: ReactNode;
      onClick: () => void;
      danger?: boolean;
    }> = [];

    entries.push({
      key: 'duplicate',
      label: 'Duplicate',
      icon: <Copy size={14} aria-hidden="true" />,
      onClick: onDuplicate,
    });

    if (!published) {
      entries.push({
        key: 'schedule',
        label: 'Schedule',
        icon: <Calendar size={14} aria-hidden="true" />,
        onClick: onSchedule,
      });
    }

    if (published && !withdrawn) {
      entries.push({
        key: 'resend',
        label: 'Resend',
        icon: <Send size={14} aria-hidden="true" />,
        onClick: onResend,
      });
      entries.push({
        key: 'withdraw',
        label: 'Withdraw',
        icon: <Undo2 size={14} aria-hidden="true" />,
        onClick: onWithdraw,
        danger: true,
      });
    }

    if (!published) {
      entries.push({
        key: 'delete',
        label: 'Delete',
        icon: <Trash2 size={14} aria-hidden="true" />,
        onClick: onDelete,
        danger: true,
      });
    }

    return entries;
  }, [detail, onDuplicate, onSchedule, onResend, onWithdraw, onDelete]);

  if (items.length === 0) return <></>;

  return (
    <div className="relative" data-overflow-menu>
      <Button
        variant="ghost"
        size="sm"
        icon={<MoreHorizontal size={14} aria-hidden="true" />}
        onClick={() => onOpenChange(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        loading={pending}
      >
        More
      </Button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-10 mt-1 min-w-[200px] rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] py-1 shadow-lg"
        >
          {items.map((item) => (
            <button
              key={item.key}
              role="menuitem"
              type="button"
              onClick={item.onClick}
              className={clsx(
                'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[color:var(--color-surface)] focus:outline-none focus-visible:bg-[color:var(--color-surface)]',
                item.danger
                  ? 'text-danger'
                  : 'text-[color:var(--color-text)]',
              )}
            >
              <span aria-hidden="true" className="-ml-0.5 shrink-0">
                {item.icon}
              </span>
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Schedule drawer
//
// PATCH /api/announcements/:id { scheduledFor }. Only meaningful before
// publish — the overflow menu hides it once `publishedAt` is set. The
// dispatch cron consumes `scheduled_for` and publishes when the moment
// arrives (`apps/api/src/cron/announcements.ts` in the same PR).
// ---------------------------------------------------------------------------

interface ScheduleDrawerProps {
  open: boolean;
  onClose: () => void;
  announcementId: string;
  currentScheduledFor: string | null;
}

/** Convert an ISO string to a value suitable for `<input type="datetime-local">`. */
const toLocalInput = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
};

/** Convert a `<input type="datetime-local">` value to an ISO string. */
const fromLocalInput = (value: string): string | null => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

function ScheduleDrawer({
  open,
  onClose,
  announcementId,
  currentScheduledFor,
}: ScheduleDrawerProps): JSX.Element {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [value, setValue] = useState(() => toLocalInput(currentScheduledFor));

  useEffect(() => {
    if (open) setValue(toLocalInput(currentScheduledFor));
  }, [open, currentScheduledFor]);

  const mutation = useMutation({
    mutationFn: async (scheduledFor: string | null) => {
      await apiRequest<{ ok: true }>(
        `/api/announcements/${encodeURIComponent(announcementId)}`,
        { method: 'PATCH', body: { scheduledFor } },
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['announcement', 'detail', announcementId],
      });
      void queryClient.invalidateQueries({ queryKey: ['announcements'] });
      toast('Schedule updated.', { variant: 'success' });
      onClose();
    },
    onError: (err) => {
      toast(
        err instanceof ApiError ? err.message : 'Could not update schedule.',
        { variant: 'error' },
      );
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const iso = fromLocalInput(value);
    if (!iso) {
      toast('Please pick a valid date and time.', { variant: 'error' });
      return;
    }
    void mutation.mutateAsync(iso);
  };

  const handleClear = (): void => {
    void mutation.mutateAsync(null);
  };

  return (
    <Drawer open={open} onClose={onClose} title="Schedule announcement" size="sm">
      <form onSubmit={handleSubmit} noValidate className="space-y-5">
        <p className="text-sm text-[color:var(--color-text-muted)]">
          Pick when the announcement should be published. It goes out
          automatically at that time.
        </p>
        <TextField
          label="Publish at"
          type="datetime-local"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <div className="flex flex-wrap justify-end gap-2 pt-2">
          {currentScheduledFor ? (
            <Button
              type="button"
              variant="ghost"
              onClick={handleClear}
              loading={mutation.isPending}
            >
              Clear schedule
            </Button>
          ) : null}
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            Save schedule
          </Button>
        </div>
      </form>
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Resend dialog
//
// Explicit scope toggle (Unread only vs All recipients) + a recipient-count
// preview so the operator knows what they're sending. Rate-limited
// server-side — a 429 with code `announcement_resend_rate_limited` is
// surfaced as the server-provided message + the `retryAfter` hint when the
// backend includes one in `ApiError.details`.
// ---------------------------------------------------------------------------

type ResendScope = 'unread' | 'all';

interface ResendDialogProps {
  open: boolean;
  onClose: () => void;
  announcementId: string;
  totalRecipients: number;
}

function ResendDialog({
  open,
  onClose,
  announcementId,
  totalRecipients,
}: ResendDialogProps): JSX.Element {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [scope, setScope] = useState<ResendScope>('unread');

  useEffect(() => {
    if (open) setScope('unread');
  }, [open]);

  // Unread preview piggybacks the existing recipients endpoint with the
  // `unread` filter — `total` is exactly the count the resend will fan to.
  const unreadPreview = useQuery({
    queryKey: ['announcement', 'recipients', announcementId, 'unread-preview'] as const,
    enabled: open,
    queryFn: ({ signal }) =>
      apiRequest<AnnouncementRecipientsResponse>(
        `/api/announcements/${encodeURIComponent(announcementId)}/recipients`,
        { signal, query: { limit: 1, offset: 0, status: 'unread' } },
      ),
    staleTime: 30_000,
  });

  const mutation = useMutation({
    mutationFn: async (resendScope: ResendScope) => {
      await apiRequest<{ ok: true; recipientCount?: number }>(
        `/api/announcements/${encodeURIComponent(announcementId)}/resend`,
        { method: 'POST', body: { scope: resendScope } },
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['announcement', 'detail', announcementId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['announcement', 'recipients', announcementId],
      });
      toast('Resend queued.', { variant: 'success' });
      onClose();
    },
    onError: (err) => {
      if (
        err instanceof ApiError &&
        err.status === 429 &&
        err.code === 'announcement_resend_rate_limited'
      ) {
        // `apiRequest` exposes the JSON body via `ApiError.details` — the
        // raw response headers (e.g. Retry-After) aren't parsed there.
        // Surface whatever the server included alongside the message.
        const retryAfterRaw = err.details?.retryAfter;
        const retryAfter =
          typeof retryAfterRaw === 'number'
            ? `${Math.max(1, Math.ceil(retryAfterRaw / 60))} min`
            : typeof retryAfterRaw === 'string'
              ? retryAfterRaw
              : null;
        toast(
          retryAfter
            ? `${err.message} Try again in ${retryAfter}.`
            : err.message,
          { variant: 'error' },
        );
        return;
      }
      toast(
        err instanceof ApiError ? err.message : 'Could not resend announcement.',
        { variant: 'error' },
      );
    },
  });

  const unreadCount = unreadPreview.data?.total ?? null;
  const previewCount = scope === 'unread' ? unreadCount : totalRecipients;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    void mutation.mutateAsync(scope);
  };

  return (
    <Drawer open={open} onClose={onClose} title="Resend announcement" size="sm">
      <form onSubmit={handleSubmit} noValidate className="space-y-5">
        <p className="text-sm text-[color:var(--color-text-muted)]">
          Choose who should receive a fresh in-app notification.
        </p>

        <fieldset>
          <legend className="text-sm font-medium">Audience</legend>
          <div
            role="radiogroup"
            aria-label="Resend audience"
            className="mt-2 flex flex-col gap-2"
          >
            <ScopeOption
              checked={scope === 'unread'}
              onChange={() => setScope('unread')}
              label="Unread only"
              caption={
                unreadCount === null
                  ? 'Counting unread recipients…'
                  : `${unreadCount} recipient${unreadCount === 1 ? '' : 's'} have not opened it yet`
              }
            />
            <ScopeOption
              checked={scope === 'all'}
              onChange={() => setScope('all')}
              label="All recipients"
              caption={`${totalRecipients} recipient${totalRecipients === 1 ? '' : 's'} on the original fanout`}
            />
          </div>
        </fieldset>

        <p
          aria-live="polite"
          className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2 text-xs text-[color:var(--color-text-muted)]"
        >
          {previewCount === null
            ? 'Calculating recipients…'
            : previewCount === 0
              ? 'Nobody will receive a notification — the audience is empty.'
              : `${previewCount} notification${previewCount === 1 ? '' : 's'} will be queued.`}
        </p>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={mutation.isPending}
            disabled={previewCount === 0}
          >
            Resend
          </Button>
        </div>
      </form>
    </Drawer>
  );
}

function ScopeOption({
  checked,
  onChange,
  label,
  caption,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
  caption: string;
}): JSX.Element {
  return (
    <label
      className={clsx(
        'flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2 transition-colors',
        checked
          ? 'border-gold-500 bg-gold-50'
          : 'border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] hover:border-gold-500',
      )}
    >
      <input
        type="radio"
        name="resend-scope"
        checked={checked}
        onChange={onChange}
        className="mt-0.5 h-4 w-4 border-[color:var(--color-border)] text-gold-700 focus:ring-gold-500"
      />
      <span className="min-w-0 text-sm">
        <span className="block font-medium text-[color:var(--color-text)]">
          {label}
        </span>
        <span className="block text-xs text-[color:var(--color-text-muted)]">
          {caption}
        </span>
      </span>
    </label>
  );
}
